import { useEffect } from 'react'
import { useEvent } from 'hooks/Utils.hooks'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ChannelTypeEnum } from '@vendorflow/types'
import { ROUTES } from 'constants/Route.constants'
import { useRedirectToOrgAndChannelBase } from 'features/firebase/PushNotifications.hooks'
import { useMyOrg, useMyOrgId } from 'queries/Auth.queries'
import { useQueryClient } from '@tanstack/react-query'
import { channelQueryFn, channelQueryKey } from 'queries/Channels.queries'
import { WorkspaceChannelParams } from 'types/Router.types'
import qs from 'qs'
import { QueryParamsEnum } from 'types/QueryParams.types'
import { useNotificationsContext } from 'features/notifications/NotificationsProvider'
import { useSessionContext } from 'features/session-manager/SessionContext'

export const CHANNEL_REDIRECT_NAMESPACE = 'ChannelRedirect'

export type ChannelRedirectData = Partial<{
  redirectToChannel: string
  redirected: boolean
}>

export default function useChannelRedirectJob() {
  const { enqueueSnackbar } = useNotificationsContext()
  const { data, forceUpdate, isBootstrapDone } = useSessionContext()
  const queryClient = useQueryClient()
  const { channelId } = useParams<WorkspaceChannelParams>()
  const envy = useMyOrg()
  const handleRedirect = useRedirectToOrgAndChannel()
  const state = data[CHANNEL_REDIRECT_NAMESPACE]

  useEffect(() => {
    if (!isBootstrapDone || !state.redirectToChannel) return

    void Promise.resolve().then(async () => {
      await performRedirect()
      forceUpdate()
    })
  }, [isBootstrapDone, state.redirectToChannel])

  useEffect(() => {
    if (!isBootstrapDone || !state.redirected) return

    void Promise.resolve().then(async () => {
      const result = channelId ? await queryClient.fetchQuery(channelQueryKey(channelId), channelQueryFn) : null
      const message = `You have been switched to ${envy?.name || 'this workspace'} to view ${
        result?.channel.name || 'this channel'
      }`
      enqueueSnackbar({ message, options: { variant: 'info' } })
    })
    delete state.redirected
    forceUpdate()
  }, [isBootstrapDone, state.redirected])

  async function performRedirect() {
    if (state.redirectToChannel) {
      await handleRedirect(state.redirectToChannel)
      delete state.redirectToChannel
    }
  }

  const execute = useEvent(async () => {
    await performRedirect()
  })

  return { execute }
}

export function useRedirectToOrgAndChannel() {
  const location = useLocation()
  const navigate = useNavigate()
  const currEnvy = useMyOrgId()
  const redirectTo = useEvent((envy, channel) => {
    const queryParams: any = qs.parse(location.search, { ignoreQueryPrefix: true })

    if (envy !== currEnvy) {
      queryParams[QueryParamsEnum.REDIRECTED] = 1
    }

    const pathname =
      (channel.channelType === ChannelTypeEnum.CAPTIVE_THREAD
        ? ROUTES.WORKSPACE_EXTERNAL_CHANNELS(envy)
        : ROUTES.WORKSPACE_INTERNAL_CHANNELS(envy)) +
      '/' +
      channel.id +
      qs.stringify(queryParams, { addQueryPrefix: true })

    if (envy === currEnvy) {
      navigate(pathname)
    } else {
      // we want to do a hard page load if we are going to a different org
      window.location.href = window.location.origin + pathname
    }
  })

  return useRedirectToOrgAndChannelBase({
    successFn: redirectTo,
    errorFn(error) {
      console.error(error)
    },
  })
}
